import { useState, useEffect } from 'react';
import { Plus, CreditCard, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge, statusBadgeVariants } from '../../components/ui/Badge';
import { Modal, ModalActions } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { Select } from '../../components/ui/Select';
import { useAuth } from '../../contexts/AuthContext';
import { getPayments, createPayment, getAllClients } from '../../lib/supabase/queries';
import { createPaymentLink } from '../../lib/stripe';
import type { Payment, Profile } from '../../types';

export function InvoicesPage() {
  const { profile, isAdmin } = useAuth();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [clients, setClients] = useState<Profile[]>([]);

  // Create invoice modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newInvoice, setNewInvoice] = useState({
    client_id: '',
    amount: '',
    description: '',
  });

  useEffect(() => {
    async function fetchData() {
      try {
        const paymentsData = await getPayments(
          isAdmin ? undefined : profile?.id
        );
        setPayments(paymentsData);

        if (isAdmin) {
          const clientsData = await getAllClients();
          setClients(clientsData);
        }
      } catch (error) {
        console.error('Error fetching payments:', error);
      }
    }

    fetchData();
  }, [profile?.id, isAdmin]);

  const handleCreateInvoice = async () => {
    if (!newInvoice.client_id || !newInvoice.amount) return;

    setIsCreating(true);
    try {
      const amountInCents = Math.round(parseFloat(newInvoice.amount) * 100);

      const payment = await createPayment({
        client_id: newInvoice.client_id,
        amount: amountInCents,
        description: newInvoice.description || null,
        status: 'pending',
        stripe_payment_id: null,
      });

      if (payment) {
        // Optionally create a Stripe payment link
        // const paymentLink = await createPaymentLink(
        //   amountInCents,
        //   newInvoice.description || 'Invoice',
        //   payment.id
        // );

        setPayments([payment, ...payments]);
        setShowCreateModal(false);
        setNewInvoice({ client_id: '', amount: '', description: '' });
      }
    } catch (error) {
      console.error('Error creating invoice:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handlePay = async (payment: Payment) => {
    try {
      const paymentLink = await createPaymentLink(
        payment.amount,
        payment.description || 'Invoice',
        payment.id
      );
      if (paymentLink) {
        window.open(paymentLink, '_blank');
      }
    } catch (error) {
      console.error('Error creating payment link:', error);
    }
  };

  // Calculate totals
  const totalPending = payments
    .filter((p) => p.status === 'pending')
    .reduce((sum, p) => sum + p.amount, 0);
  const totalPaid = payments
    .filter((p) => p.status === 'paid')
    .reduce((sum, p) => sum + p.amount, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-obsidian dark:text-signal">
            Invoices
          </h1>
          <p className="mt-1 text-slate-600 dark:text-slate-400">
            {isAdmin ? 'Manage client invoices and payments' : 'View and pay your invoices'}
          </p>
        </div>
        {isAdmin && (
          <Button
            leftIcon={<Plus className="w-4 h-4" />}
            onClick={() => setShowCreateModal(true)}
          >
            Create Invoice
          </Button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card variant="bordered" padding="md">
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Outstanding
                </p>
                <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400 mt-1">
                  ${(totalPending / 100).toLocaleString()}
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card variant="bordered" padding="md">
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Paid
                </p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
                  ${(totalPaid / 100).toLocaleString()}
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card variant="bordered" padding="md">
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Total Invoices
                </p>
                <p className="text-2xl font-bold text-obsidian dark:text-signal mt-1">
                  {payments.length}
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-slate-600 dark:text-slate-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Invoices List */}
      <Card variant="bordered" padding="none">
        <CardHeader className="p-6 border-b border-slate-200 dark:border-slate-700">
          <CardTitle>All Invoices</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {payments.length === 0 ? (
            <div className="p-8 text-center text-slate-500 dark:text-slate-400">
              <CreditCard className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No invoices yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-700">
                    <th className="text-left p-4 text-sm font-medium text-slate-600 dark:text-slate-400">
                      Date
                    </th>
                    {isAdmin && (
                      <th className="text-left p-4 text-sm font-medium text-slate-600 dark:text-slate-400">
                        Client
                      </th>
                    )}
                    <th className="text-left p-4 text-sm font-medium text-slate-600 dark:text-slate-400">
                      Description
                    </th>
                    <th className="text-right p-4 text-sm font-medium text-slate-600 dark:text-slate-400">
                      Amount
                    </th>
                    <th className="text-center p-4 text-sm font-medium text-slate-600 dark:text-slate-400">
                      Status
                    </th>
                    <th className="text-right p-4 text-sm font-medium text-slate-600 dark:text-slate-400">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {payments.map((payment) => (
                    <tr
                      key={payment.id}
                      className="hover:bg-slate-50 dark:hover:bg-obsidian-100"
                    >
                      <td className="p-4 text-sm text-obsidian dark:text-signal">
                        {format(new Date(payment.created_at), 'MMM d, yyyy')}
                      </td>
                      {isAdmin && (
                        <td className="p-4 text-sm text-slate-700 dark:text-slate-300">
                          {payment.client?.company_name || 'Unknown'}
                        </td>
                      )}
                      <td className="p-4 text-sm text-slate-700 dark:text-slate-300">
                        {payment.description || 'Invoice'}
                      </td>
                      <td className="p-4 text-sm text-obsidian dark:text-signal text-right font-medium">
                        ${(payment.amount / 100).toLocaleString()}
                      </td>
                      <td className="p-4 text-center">
                        <Badge
                          variant={statusBadgeVariants[payment.status] || 'default'}
                        >
                          {payment.status}
                        </Badge>
                      </td>
                      <td className="p-4 text-right">
                        {payment.status === 'pending' && !isAdmin && (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handlePay(payment)}
                          >
                            Pay Now
                          </Button>
                        )}
                        {payment.stripe_payment_id && (
                          <a
                            href={`https://dashboard.stripe.com/payments/${payment.stripe_payment_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-sm text-amber dark:text-amber hover:underline"
                          >
                            View
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Invoice Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create Invoice"
        description="Send an invoice to a client"
      >
        <div className="space-y-4">
          <Select
            label="Client"
            value={newInvoice.client_id}
            onChange={(e) =>
              setNewInvoice({ ...newInvoice, client_id: e.target.value })
            }
            options={clients.map((c) => ({
              value: c.id,
              label: c.company_name || c.id,
            }))}
            placeholder="Select a client"
          />
          <Input
            label="Amount (USD)"
            type="number"
            min="0"
            step="0.01"
            value={newInvoice.amount}
            onChange={(e) =>
              setNewInvoice({ ...newInvoice, amount: e.target.value })
            }
            placeholder="0.00"
          />
          <Textarea
            label="Description"
            value={newInvoice.description}
            onChange={(e) =>
              setNewInvoice({ ...newInvoice, description: e.target.value })
            }
            placeholder="e.g., Churn prediction model - Phase 1"
          />
        </div>
        <ModalActions>
          <Button variant="outline" onClick={() => setShowCreateModal(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateInvoice}
            isLoading={isCreating}
            disabled={!newInvoice.client_id || !newInvoice.amount}
          >
            Create Invoice
          </Button>
        </ModalActions>
      </Modal>
    </div>
  );
}

export default InvoicesPage;
